import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useHttp } from 'shared/providers/provide-http';
import { TableDataContext } from 'shared/types';

type SubmitNewProps<T, U, V> = {
    mapper: (submission: T) => U;
    name: string;
    successRoute?: string;
    context: React.Context<TableDataContext<V>>;
    onSuccess?: (submission: T) => void;
};

export default function useSubmitNew<FormData, Payload, Data>({
    mapper,
    name,
    context,
    successRoute,
    onSuccess,
}: SubmitNewProps<FormData, Payload, Data>) {
    const http = useHttp();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { refreshData, massActionModal } = useContext(context);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>();

    const onSubmit = async (submission: FormData) => {
        setIsLoading(true);
        try {
            const body: Payload = mapper(submission);

            await http.post(`/api/${name}`, body);

            refreshData();

            if (onSuccess) {
                onSuccess(submission);
            }

            enqueueSnackbar('Save successful', { variant: 'success' });
            if (!!successRoute) history.push(successRoute);
            massActionModal.handleMassActionModalClose();
        } catch (error) {
            const axiosErrorResponse = (error as AxiosError).response;
            const errorText =
                axiosErrorResponse?.status === 400 && axiosErrorResponse?.data
                    ? (axiosErrorResponse?.data as string)
                    : `${axiosErrorResponse?.statusText} - ${axiosErrorResponse?.status}`;

            setError(errorText);
            enqueueSnackbar('Save failed', { variant: 'error' });
        }

        if (!successRoute) setIsLoading(false);
    };

    return {
        onSubmit,
        isLoading,
        error,
    };
}
